import { memo, useMemo } from "react";
import {
    TaskStatus as TaskStatusEnum,
    UI_VISIBLE_TASK_STATUSES,
} from "@common/types/task";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { Picker } from "@design-system/Picker/Picker.lib";
import cellStyles from "@design-system/ViewTable/EditableCellContent.module.scss";
import { TaskStatus } from "./TaskStatus";
import { TASK_STATUS_CONFIG } from "./taskStatus.constants";

interface TaskStatusSelectProps {
    value: TaskStatusEnum | null | undefined;
    onValueChange: (value: TaskStatusEnum) => void;
    placeholder?: string;
    /** A Picker trigger part (e.g. `Picker.CellContent`) replacing the default button. */
    trigger?: React.ReactNode;
    buttonProps?: TooltipTriggerButtonProps;
}

export const TaskStatusSelect = memo(
    ({
        value,
        onValueChange,
        placeholder = "Set status",
        trigger,
        buttonProps,
    }: TaskStatusSelectProps) => {
        const {
            tooltip: triggerTooltip,
            variant: triggerVariant = "primary",
            ...triggerButtonRest
        } = buttonProps ?? {};
        const triggerContent = useMemo(
            () => (value ? <TaskStatus status={value} showText /> : null),
            [value],
        );

        return (
            <Picker.Select
                value={value ?? null}
                onValueChange={(status) => {
                    if (status) {
                        onValueChange(status);
                    }
                }}
            >
                {trigger ?? (
                    <Picker.ButtonTrigger
                        render={
                            <Picker.Button
                                aria-label={
                                    value
                                        ? TASK_STATUS_CONFIG[value].tooltip
                                        : undefined
                                }
                                className={cellStyles["editable-cell-content"]}
                                {...triggerButtonRest}
                                variant={triggerVariant}
                                size={triggerButtonRest.size ?? "md"}
                            />
                        }
                        value={triggerContent ?? undefined}
                        placeholder={placeholder}
                        tooltip={triggerTooltip}
                    />
                )}
                <Picker.Popup sideOffset={4}>
                    <Picker.List>
                        {UI_VISIBLE_TASK_STATUSES.map((status) => (
                            <Picker.Item key={status} value={status}>
                                {TASK_STATUS_CONFIG[status].tooltip}
                            </Picker.Item>
                        ))}
                    </Picker.List>
                </Picker.Popup>
            </Picker.Select>
        );
    },
);

TaskStatusSelect.displayName = "TaskStatusSelect";
